import { Fragment } from "react";
import { Droppable, Draggable } from "@hello-pangea/dnd";
import { DropdownMenu, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, GripVertical, Trash2, Eye, EyeOff, PencilLine, ChevronDown } from "lucide-react";
import { SUBTYPE_META } from "@/components/sectionTypes";

/**
 * Draggable Table of Contents for the Blog Editor. Renders one row per section
 * with a level chip, subtype pill, heading, hidden badge and per-row hover
 * actions (toggle visibility · edit · delete). Rows are drag-reorderable and
 * clicking a row opens the inline section editor. Insert dividers appear
 * between rows on hover.
 */
export default function BlogEditorTOC({
  sections,
  editingSectionId,
  SubtypeMenu,
  InsertDivider,
  addSection,
  openSection,
  scrollPreviewTo,
  updateSection,
  removeSectionWithToast,
  confirmDelete,
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-widest text-slate-500">
          Contents ({sections.length}) <span className="text-slate-400 normal-case">— drag to reorder · click a title to edit</span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="editor-add-section" className="text-xs text-sky-700 hover:text-sky-900 inline-flex items-center gap-1">
              <Plus className="w-3.5 h-3.5"/>Add section<ChevronDown className="w-3 h-3"/>
            </button>
          </DropdownMenuTrigger>
          <SubtypeMenu onPick={(sub) => addSection(sub)}/>
        </DropdownMenu>
      </div>
      <Droppable droppableId="toc-sections">
        {(prov) => (
          <div ref={prov.innerRef} {...prov.droppableProps} className="space-y-1">
            {sections.map((s, i) => (
              <Fragment key={s.id || `t-${i}`}>
                {i === 0 && <InsertDivider index={0} variant="toc"/>}
                <Draggable draggableId={`t-${String(s.id || i)}`} index={i}>
                  {(dp, snap) => (
                    <div ref={dp.innerRef} {...dp.draggableProps}
                         data-testid={`toc-section-${s.id}`}
                         className={`group flex items-center gap-2 rounded-md px-2 py-1.5 border transition-colors ${
                           snap.isDragging ? "border-sky-300 shadow-md bg-white"
                             : editingSectionId === s.id
                               ? "border-sky-300 bg-sky-50"
                               : "border-transparent hover:border-slate-200 hover:bg-slate-50"
                         } ${s.level === 1 ? "font-semibold" : s.level === 3 ? "pl-6" : "pl-3"} ${
                           s.hidden ? "opacity-50" : ""}`}>
                      <span {...dp.dragHandleProps} className="text-slate-300 hover:text-slate-600 cursor-grab active:cursor-grabbing" aria-label="Drag section">
                        <GripVertical className="w-4 h-4"/>
                      </span>
                      <span className="text-[10px] uppercase tracking-widest text-slate-400 w-6 shrink-0">H{s.level}</span>
                      {s.subtype && s.subtype !== "prose" && (
                        <span className="text-[9px] uppercase tracking-widest bg-slate-100 text-slate-600 rounded px-1 py-0.5 shrink-0">
                          {(SUBTYPE_META[s.subtype]?.label || s.subtype)}
                        </span>
                      )}
                      {s.hidden && (
                        <span className="text-[9px] uppercase tracking-widest bg-amber-100 text-amber-800 rounded px-1 py-0.5 shrink-0">Hidden</span>
                      )}
                      <button
                        onClick={() => { openSection(s.id); scrollPreviewTo(s.id); }}
                        onDoubleClick={() => scrollPreviewTo(s.id)}
                        className={`flex-1 text-left text-sm truncate ${editingSectionId === s.id ? "text-sky-800 font-semibold" : "text-slate-700 hover:text-slate-900"} ${s.hidden ? "line-through" : ""}`}
                        title="Click to edit · double-click to scroll preview">
                        {s.heading || <span className="italic text-slate-400">Untitled section</span>}
                      </button>
                      <button
                        onClick={() => updateSection(i, "hidden", !s.hidden)}
                        data-testid={`toc-toggle-hidden-${s.id}`}
                        title={s.hidden ? "Show section" : "Hide section"}
                        className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-sky-700 transition-opacity">
                        {s.hidden ? <EyeOff className="w-3.5 h-3.5"/> : <Eye className="w-3.5 h-3.5"/>}
                      </button>
                      <button
                        onClick={() => openSection(s.id)}
                        className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-sky-700 transition-opacity"
                        aria-label="Edit section">
                        <PencilLine className="w-3.5 h-3.5"/>
                      </button>
                      <button
                        onClick={() => confirmDelete(
                          "Delete section?",
                          `"${s.heading || "Untitled section"}" will be removed. You can undo with Ctrl+Z.`,
                          () => removeSectionWithToast(s.id, s.heading),
                        )}
                        className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 transition-opacity"
                        aria-label="Delete section">
                        <Trash2 className="w-3.5 h-3.5"/>
                      </button>
                    </div>
                  )}
                </Draggable>
                <InsertDivider index={i + 1} variant="toc"/>
              </Fragment>
            ))}
            {prov.placeholder}
            {sections.length === 0 && (
              <div className="text-sm text-slate-400 italic px-2 py-2">No sections yet. Use "Add section" above.</div>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
}
